/**
 * PATH: packages/email-hub/src/feature/components/AllocateBoardDialog.tsx
 * FILE_ID: EMAILHUB-ALLOCATE-BOARD-DIALOG-001
 * PURPOSE: Keyboard entry for filing an email onto a Kanban board.
 * WHY: Operators asked for A to open allocate-to-board without the mouse.
 * NOTES FOR FUTURE AI: The picker is AllocateChip embedded. Hide does not
 *   delete the email. Ctrl/Cmd+A must stay native select-all.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'

import { AllocateChip } from '@quotech/kanban-host'
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'
import type { KanbanApi } from '@quotech/ui/kanban'

export function AllocateBoardDialog({
  emailId,
  alreadyAllocated,
  api,
  orgId,
  onClose,
  onAllocated,
}: {
  emailId: string
  alreadyAllocated: boolean
  api: KanbanApi
  orgId: number | null
  onClose: () => void
  onAllocated: (summary?: EmailAllocationSummary) => void
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Allocate to a Kanban board"
      data-testid="allocate-board-dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-sm rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-foreground">
              Allocate to a Kanban board
            </div>
            <p className="text-[11px] text-muted-foreground">
              Pick a board, then a lane. Esc closes.
            </p>
          </div>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted"
            aria-label="Close allocate dialog"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-3">
          <AllocateChip
            embedded
            emailId={emailId}
            api={api}
            orgId={orgId}
            allowAdditional={alreadyAllocated}
            onAllocated={(summary) => {
              onAllocated(summary)
              onClose()
            }}
          />
        </div>
      </div>
    </div>
  )
}
